import api from '../../../api';

/**
 * Analytics API client for buyer portal analytics
 */

const ANALYTICS_BASE_URL = '/api/buyer-portal/analytics';
const SENTRY_BASE_URL = '/api/buyer-portal/sentry';
const BUYER_PORTAL_BASE_URL = '/api/buyer-portal';

/**
 * Track a buyer event
 * @param {Object} eventData - Event data to track
 * @returns {Promise}
 */
export const trackEvent = async (eventData) => {
    try {
        const response = await api.post(`${ANALYTICS_BASE_URL}/track/`, eventData);
        return response.data;
    } catch (error) {
        console.error('Error tracking event:', error);
        throw error;
    }
};

/**
 * Fetch dashboard metrics
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @returns {Promise}
 */
export const fetchDashboardMetrics = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;

        const response = await api.get(`${ANALYTICS_BASE_URL}/dashboard/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching dashboard metrics:', error);
        throw error;
    }
};

/**
 * Fetch the Daily Report — snapshot-backed period view + period-over-period
 * comparison + rules-based insights.
 * @param {('week'|'month'|'quarter'|'6month'|'year')} period
 * @returns {Promise}
 */
export const fetchDailyReport = async (period = 'week') => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/daily-report/`, {
            params: { period },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching daily report:', error);
        throw error;
    }
};

/**
 * Fetch buyer location analytics — counts by country / state / city (IP-derived)
 * plus map points. Powers the Analytics "Location" tab.
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @returns {Promise}
 */
export const fetchBuyerLocations = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/locations/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching buyer locations:', error);
        throw error;
    }
};

/**
 * Drill down to the buyers behind a country/state/city slice on the Location tab.
 * @param {Object} opts
 * @param {('country'|'state'|'city')} opts.level
 * @param {string} opts.value - the place name (or country code)
 * @param {string} [opts.region] - optional state to disambiguate a city
 * @param {string} [opts.startDate]
 * @param {string} [opts.endDate]
 * @returns {Promise}
 */
export const fetchBuyerLocationDetail = async ({ level, value, region, startDate, endDate }) => {
    try {
        const params = { detail: 'buyers', level, value };
        if (region) params.region = region;
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/locations/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching buyer location detail:', error);
        throw error;
    }
};

/**
 * Fetch buyer heat scores
 * @param {number} limit - Number of buyers to return
 * @param {number} days - Number of days to look back
 * @returns {Promise}
 */
export const fetchBuyerHeatScores = async (limit = 50, days = 30) => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/buyer-heat-scores/`, {
            params: { limit, days }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching buyer heat scores:', error);
        throw error;
    }
};

/**
 * Fetch "Top Engaged Buyers (Buyer Intent Score)" — the 0-100 tiered intent
 * score, week-over-week trend, finance/timeline labels and the
 * "likely to buy in 90 days" flag, computed nightly.
 * @param {number} limit - Number of buyers to return
 * @param {number} days - Activity look-back window for the activity summary
 * @returns {Promise<{generated_at, window_days, count, likely_count, buyers: []}>}
 */
export const fetchTopEngagedBuyers = async (limit = 50, days = 30) => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/top-engaged-buyers/`, {
            params: { limit, days }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching top engaged buyers:', error);
        throw error;
    }
};

/**
 * Fetch source quality metrics
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @returns {Promise}
 */
export const fetchSourceQuality = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;

        const response = await api.get(`${ANALYTICS_BASE_URL}/source-quality/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching source quality:', error);
        throw error;
    }
};

/**
 * Fetch event statistics
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @returns {Promise}
 */
export const fetchEventStats = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;

        const response = await api.get(`${ANALYTICS_BASE_URL}/event-stats/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching event stats:', error);
        throw error;
    }
};

/**
 * Fetch property interaction statistics
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @param {number} limit - Number of properties to return
 * @param {string} sort - Sort order (total_interactions, gallery_views, intent_clicks, enquiries_sent, newest, oldest)
 * @param {string} status - Lifecycle filter (current, sold, withdrawn)
 * @param {string} search - Filter by address, title, suburb, or street (optional)
 * @returns {Promise}
 */
export const fetchPropertyInteractions = async (startDate, endDate, limit = 50, sort = 'total_interactions', status = 'current', search = '') => {
    try {
        const params = { limit, sort, status };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        if (search && search.trim()) params.search = search.trim();

        const response = await api.get(`${ANALYTICS_BASE_URL}/property-interactions/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching property interactions:', error);
        throw error;
    }
};

/**
 * Fetch Collings portal-attributed sales analytics
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @returns {Promise}
 */
export const fetchPortalSalesAnalytics = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;

        const response = await api.get(`${ANALYTICS_BASE_URL}/portal-sales/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching portal sales analytics:', error);
        throw error;
    }
};

/**
 * Fetch the DOMA-influenced revenue attribution breakdown — portal-attributed
 * sales split into direct / assisted / analytics / returning categories plus
 * the Total DOMA Influenced Revenue headline.
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @returns {Promise}
 */
export const fetchPortalAttributionAnalytics = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;

        const response = await api.get(`${ANALYTICS_BASE_URL}/portal-attribution/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching portal attribution analytics:', error);
        throw error;
    }
};

/**
 * Fetch detailed event list
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @param {string} eventName - Filter by event name (optional)
 * @param {number} propertyId - Filter by property ID (optional)
 * @param {number} buyerId - Filter by buyer ID (optional)
 * @param {number} limit - Number of events to return
 * @returns {Promise}
 */
export const fetchEventDetails = async (startDate, endDate, eventName = null, propertyId = null, buyerId = null, limit = 100, source = null, mode = null) => {
    try {
        const params = { limit };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        if (eventName) params.event_name = eventName;
        if (propertyId) params.property_id = propertyId;
        if (buyerId) params.buyer_id = buyerId;
        if (source) params.source = source;
        if (mode) params.mode = mode;

        const response = await api.get(`${ANALYTICS_BASE_URL}/events/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching event details:', error);
        throw error;
    }
};

/**
 * The buyers behind an Overview card, one row each, for a date range.
 * @param {string} segment - 'active' | 'viewers' | 'signups'
 * @param {string} startDate - ISO date string
 * @param {string} endDate - ISO date string
 * @returns {Promise}
 */
// Every property save in the range, newest first (Save Rate card, Monday 13391600148).
export const fetchPropertySaves = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/property-saves/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching property saves:', error);
        throw error;
    }
};

export const fetchBuyerEngagement = async (segment, startDate, endDate) => {
    try {
        const params = { segment };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/buyer-engagement/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching buyer engagement:', error);
        throw error;
    }
};

/**
 * Fetch buyer activity details
 * @param {number} buyerId - Buyer ID
 * @returns {Promise}
 */
export const fetchBuyerActivity = async (buyerId) => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/buyer-activity/${buyerId}/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching buyer activity:', error);
        throw error;
    }
};

/**
 * Fetch enquiry details
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @param {number} limit - Number of enquiries to return
 * @returns {Promise}
 */
export const fetchEnquiryDetails = async (startDate, endDate, limit = 100) => {
    try {
        const params = { limit };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;

        const response = await api.get(`${ANALYTICS_BASE_URL}/enquiries/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching enquiry details:', error);
        throw error;
    }
};

/**
 * Fetch offer details
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @param {number} limit - Number of offers to return
 * @returns {Promise}
 */
export const fetchOfferDetails = async (startDate, endDate, limit = 100) => {
    try {
        const params = { limit };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;

        const response = await api.get(`${ANALYTICS_BASE_URL}/offers/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching offer details:', error);
        throw error;
    }
};

/**
 * Fetch Sentry issues
 * @param {number} limit - Number of issues to return
 * @param {string} status - Filter by status (unresolved, resolved, ignored)
 * @param {string} query - Search query string
 * @returns {Promise}
 */
export const fetchSentryIssues = async (limit = 50, status = 'unresolved', query = '') => {
    try {
        const params = { limit, status };
        if (query) params.query = query;

        const response = await api.get(`${SENTRY_BASE_URL}/issues/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching Sentry issues:', error);
        throw error;
    }
};

/**
 * Fetch Sentry statistics
 * @returns {Promise}
 */
export const fetchSentryStats = async () => {
    try {
        const response = await api.get(`${SENTRY_BASE_URL}/stats/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching Sentry stats:', error);
        throw error;
    }
};
/**
 * Fetch live active users count (sessions active in last 5 minutes)
 * @returns {Promise<{active_users: number, window_minutes: number}>}
 */
export const fetchActiveUsers = async () => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/active-users/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching active users:', error);
        throw error;
    }
};

/**
 * Fetch session analytics (list of buyer-portal sessions with duration & event counts)
 * @param {string} startDate - ISO datetime
 * @param {string} endDate - ISO datetime
 * @param {number} limit - Number of sessions to return
 * @returns {Promise}
 */
export const fetchSessionAnalytics = async (startDate, endDate, limit = 200) => {
    try {
        const params = { limit };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;

        const response = await api.get(`${ANALYTICS_BASE_URL}/sessions/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching session analytics:', error);
        throw error;
    }
};

/**
 * Fetch buyer preferences analytics
 * @param {string} [startDate] - ISO datetime
 * @param {string} [endDate] - ISO datetime
 * @returns {Promise}
 */
export const fetchBuyerPreferencesAnalytics = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/buyer-preferences/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching buyer preferences analytics:', error);
        throw error;
    }
};

/**
 * Fetch Investment Calculator usage analytics
 * @param {string} startDate - ISO datetime
 * @param {string} endDate - ISO datetime
 * @returns {Promise}
 */
export const fetchInvestmentCalculatorAnalytics = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/investment-calculator/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching investment-calculator analytics:', error);
        throw error;
    }
};

/**
 * Fetch Mortgage Calculator usage analytics
 * @param {string} startDate - ISO datetime
 * @param {string} endDate - ISO datetime
 * @returns {Promise}
 */
export const fetchMortgageCalculatorAnalytics = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/mortgage-calculator/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching mortgage-calculator analytics:', error);
        throw error;
    }
};

/**
 * Fetch SMSF page analytics (eligibility quiz, calculator, enquiries, specialists)
 * @param {string} startDate - ISO datetime
 * @param {string} endDate - ISO datetime
 * @returns {Promise}
 */
export const fetchSmsfAnalytics = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/smsf/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching smsf analytics:', error);
        throw error;
    }
};

/**
 * Fetch buyers whose BuyingPreference matches the given filter (drill-down).
 * @param {string} filterType - one of: suburb, property_type, bedrooms, price_max, land_size, timeline, buyer_type, finance_status
 * @param {string} filterValue - the value to match (e.g. "Melbourne", "House", "2", "$500,000")
 * @param {string} [startDate] - ISO datetime
 * @param {string} [endDate] - ISO datetime
 * @returns {Promise}
 */
export const fetchBuyerPreferenceMatches = async (filterType, filterValue, startDate, endDate) => {
    try {
        const params = { filter_type: filterType, filter_value: filterValue };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/buyer-preferences/matches/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching buyer preference matches:', error);
        throw error;
    }
};

// ── Find Buyers for a Property ─────────────────────────────────────────────
// Pick a listing (or enter it by hand), tick the suburbs that count as relevant
// (radius pre-select + broad areas like "Melbourne"), then rank the registered
// buyers whose saved preferences fit. All admin/agent, read-only.

/**
 * Search Collings sale listings for the property picker.
 * @param {string} q - address / suburb / title search text
 * @returns {Promise<{count, results: Array}>}
 */
export const fetchFindBuyersListings = async (q = '') => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/find-buyers/listings/`, {
            params: q ? { q } : {},
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching find-buyers listings:', error);
        throw error;
    }
};

/**
 * Fetch the suburb tick-box list: every suburb buyers have marked, with a buyer
 * count and a proximity tag (exact / surrounding / other) relative to the target.
 * `recommended` rows (target + within radius) are what the UI pre-ticks.
 * @param {{ suburb?, state?, lat?, lng?, radiusKm? }} opts
 * @returns {Promise<{radius_km, origin_resolved, target, suburbs: Array}>}
 */
export const fetchFindBuyersSuburbs = async ({ suburb, state, lat, lng, radiusKm = 5 } = {}) => {
    try {
        const params = { radius_km: radiusKm };
        if (suburb) params.suburb = suburb;
        if (state) params.state = state;
        if (lat != null) params.lat = lat;
        if (lng != null) params.lng = lng;
        const response = await api.get(`${ANALYTICS_BASE_URL}/find-buyers/suburbs/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching find-buyers suburbs:', error);
        throw error;
    }
};

/**
 * Rank the buyers whose preferences match the chosen property + ticked suburbs.
 * @param {Object} payload - { property_id?, suburb, state, lat, lng, radius_km,
 *   price, bedrooms, property_types: [..], portal_category, suburbs: [..], strict }
 * @returns {Promise<{target, selected_suburbs, count, results: Array}>}
 */
export const findBuyersForProperty = async (payload) => {
    try {
        const response = await api.post(`${ANALYTICS_BASE_URL}/find-buyers/`, payload);
        return response.data;
    } catch (error) {
        console.error('Error finding buyers for property:', error);
        throw error;
    }
};

/**
 * Generate the "Buyer Demand" PDF from a Find Buyers search — the listing-
 * presentation document that proves the buyer pool for a property.
 *
 * The search is re-run on the server from these criteria, so the PDF always
 * agrees with the list on screen.
 *
 * @param {Object} payload - the same search criteria findBuyersForProperty takes,
 *   plus { mode: 'vendor'|'internal', property_address, prepared_for, why_text,
 *   notes, email_to, sections, hidden_readiness, hidden_suburbs,
 *   hidden_price_bands }. mode 'internal' includes buyer names and is admin-only.
 * @returns {Promise<{filename, count, pdf_base64}>} or { status: 'sent' } when
 *   email_to is set.
 */
export const generateBuyerDemandReport = async (payload) => {
    try {
        const response = await api.post(`${ANALYTICS_BASE_URL}/find-buyers/report/`, payload);
        return response.data;
    } catch (error) {
        console.error('Error generating buyer demand report:', error);
        throw error;
    }
};

/**
 * What the buyer demand report WOULD contain for this search — the sections,
 * and the individual readiness lines, suburbs and budget bands. Feeds the tick
 * boxes in the report modal so the agent chooses what the vendor sees before
 * the PDF is generated. Aggregate counts only, no buyers.
 *
 * @param {Object} criteria - the same search criteria the report takes
 * @returns {Promise<{count, sections, default_sections, readiness, suburbs, price_bands}>}
 */
export const previewBuyerDemandReport = async (criteria) => {
    try {
        const response = await api.post(`${ANALYTICS_BASE_URL}/find-buyers/report/`,
            { ...criteria, preview: true });
        return response.data;
    } catch (error) {
        console.error('Error loading buyer demand report options:', error);
        throw error;
    }
};

/**
 * Edit a matched buyer's saved preferences from the results list. Writes to the
 * right database for the buyer's source (portal / crm / database).
 * @param {Object} payload - { source, ref_id, suburbs:[], price_min, price_max,
 *   beds_min, beds_max, baths, cars, property_types:[] }
 * @returns {Promise}
 */
export const editBuyerPreferences = async (payload) => {
    try {
        const response = await api.patch(`${ANALYTICS_BASE_URL}/find-buyers/preferences/`, payload);
        return response.data;
    } catch (error) {
        console.error('Error editing buyer preferences:', error);
        throw error;
    }
};

/**
 * The "Purchased" section of Find Buyers (CRM-2026-00049): buyers who told an
 * agent they have already bought and are no longer looking.
 * @returns {Promise<{count, results: Array}>}
 */
export const fetchPurchasedBuyers = async () => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/find-buyers/purchased/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching purchased buyers:', error);
        throw error;
    }
};

/**
 * Move a buyer into (or back out of) the Purchased section. Reversible.
 * Flagging drops them out of every active buyer list at once.
 * @param {{ contactId?: number, buyerId?: number, purchased?: boolean,
 *           purchaseDate?: string }} opts - purchaseDate is "YYYY-MM-DD"; omit
 *   it entirely to leave a date someone typed earlier alone.
 * @returns {Promise<{updated, purchased, buyer}>}
 */
export const setBuyerPurchased = async ({ contactId, buyerId, purchased = true, purchaseDate } = {}) => {
    try {
        const payload = { purchased };
        if (contactId) payload.contact_id = contactId;
        if (buyerId) payload.buyer_id = buyerId;
        if (purchaseDate !== undefined) payload.purchase_date = purchaseDate || '';
        const response = await api.post(`${ANALYTICS_BASE_URL}/find-buyers/purchased/`, payload);
        return response.data;
    } catch (error) {
        console.error('Error updating purchased buyer:', error);
        throw error;
    }
};

/**
 * Mark a buyer Recently rented, or take them back out (CRM-2026-00132). They
 * leave every buyer list and alert until the return date, then go back to
 * Actively looking, and the agent gets a call-back reminder for that day.
 * @param {{ contactId?: number, buyerId?: number, rented?: boolean,
 *           returnOption?: '6m'|'12m'|'date', returnDate?: string }} opts -
 *   returnDate is "YYYY-MM-DD" and only read with returnOption 'date'.
 * @returns {Promise<{updated, rented, status}>}
 */
export const setBuyerRented = async ({ contactId, buyerId, rented = true, returnOption, returnDate } = {}) => {
    const payload = { rented };
    if (contactId) payload.contact_id = contactId;
    if (buyerId) payload.buyer_id = buyerId;
    if (rented && returnOption) payload.return_option = returnOption;
    if (rented && returnOption === 'date' && returnDate) payload.return_date = returnDate;
    const response = await api.post(`${ANALYTICS_BASE_URL}/find-buyers/rented/`, payload);
    return response.data;
};

/**
 * Read a buyer's full search status as the portal records it, keyed by their
 * CRM contact (CRM-2026-00097, CG). This is the same four-state status the buyer
 * sets on the portal's Buying Preferences page - Actively looking, Keeping an
 * eye on the market, Paused, Purchased, No longer looking - not the older
 * binary Recently Purchased flag.
 * @param {number} contactId
 * @returns {Promise<{status: Object, options: Object}>} snapshot + the labelled
 *   status/reason/pause vocabulary the control renders.
 */
export const getContactSearchStatus = async (contactId) => {
    const response = await api.get(`${BUYER_PORTAL_BASE_URL}/contacts/${contactId}/search-status/`);
    return response.data;
};

/**
 * Set a buyer's search status from the CRM (source='agent'). Writes onto every
 * record the person has and mirrors the purchased branch down to the older
 * Recently Purchased flag server-side, so the Buyers page and this stay in step.
 * @param {number} contactId
 * @param {Object} payload - { status, pause_option?, paused_until?, reason?,
 *   note?, purchase_suburb?, purchase_date?, bought_through_collings? }
 * @returns {Promise<{status: Object, options: Object}>}
 */
export const setContactSearchStatus = async (contactId, payload) => {
    const response = await api.post(`${BUYER_PORTAL_BASE_URL}/contacts/${contactId}/search-status/`, payload);
    return response.data;
};

/**
 * Email or text all the matched buyers (or the shortlist) in one send. Routes
 * through the Communications engine, so consent, the SMS opt-out link and the
 * global send kill switch all apply — the response's `simulated` flag reports
 * whether anything actually went out.
 * @param {Object} payload - { channel: 'email'|'sms', subject, body,
 *   recipients: [{ source, ref_id, buyer_id, contact_id, email, phone, name }] }
 * There is no recipient ceiling: whatever the search matched can be messaged.
 * Because that can run to thousands of people, the backend answers 202 with
 * `{ queued: true, total, reachable }` and sends on a worker, so the composer
 * reports "sending in the background" rather than waiting on the last message.
 * @returns {Promise<{total, reachable, queued, sent, failed, skipped, simulated}>}
 */
export const messageBuyerMatches = async (payload) => {
    try {
        const response = await api.post(`${ANALYTICS_BASE_URL}/find-buyers/message/`, payload);
        return response.data;
    } catch (error) {
        console.error('Error messaging buyer matches:', error);
        throw error;
    }
};

/**
 * Send ONE test copy of the composed Find-Buyers message to an address or
 * mobile number the agent types in, before they blast the matched list
 * (CG, 2026-08-27: "I would like to see how the sms/email comes out").
 *
 * It renders exactly what a matched buyer would get — same image, same Off
 * Market Portal invitation, same unsubscribe button — and delivers even while
 * the global send kill switch is off, because a preview that only simulates is
 * no preview at all.
 * @param {Object} payload - { channel: 'email'|'sms', subject, body, to,
 *   sample?: { source, ref_id, name } }
 * @returns {Promise<{sent: boolean, to: string, status: string, error: string}>}
 */
export const sendTestBuyerMatchMessage = async (payload) => {
    try {
        const response = await api.post(`${ANALYTICS_BASE_URL}/find-buyers/message/test/`, payload);
        return response.data;
    } catch (error) {
        console.error('Error sending test message:', error);
        throw error;
    }
};

// ── Renter Analytics ─────────────────────────────────────────────────────────
// The renter counterpart of fetchBuyerPreferencesAnalytics/fetchBuyerPreferenceMatches
// above (Monday 12908991007, 2026-08-27: "Create Renter Analytic, similar to
// buyer analytics"), trimmed to the fields RentingPreference actually has.

/**
 * Fetch renter preferences analytics.
 * @param {string} [startDate] - ISO datetime
 * @param {string} [endDate] - ISO datetime
 * @returns {Promise}
 */
export const fetchRentingPreferencesAnalytics = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/renting-preferences/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching renter preferences analytics:', error);
        throw error;
    }
};

/**
 * Fetch renters whose RentingPreference matches the given filter (drill-down).
 * @param {string} filterType - one of: suburb, city, property_type, bedrooms, rent_max
 * @param {string} filterValue - the value to match
 * @param {string} [startDate] - ISO datetime
 * @param {string} [endDate] - ISO datetime
 * @returns {Promise}
 */
export const fetchRentingPreferenceMatches = async (filterType, filterValue, startDate, endDate) => {
    try {
        const params = { filter_type: filterType, filter_value: filterValue };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/renting-preferences/matches/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching renter preference matches:', error);
        throw error;
    }
};

// ── Find Renters for a Property (Renter Match tab) ──────────────────────────
// The lease-listing sibling of Find Buyers above (Monday 12908991007): pick a
// rental listing, tick suburbs, rank the renters whose saved search fits.
// v1 has one source (RentingPreference) rather than three, and no PDF report
// or "send test message" yet (that endpoint doesn't exist on the buy side
// either - buyer_portal has no message/test/ route).

/**
 * Search Collings LEASE listings for the property picker.
 * @param {string} q - address / suburb / title search text
 * @returns {Promise<{count, results: Array}>}
 */
export const fetchFindRentersListings = async (q = '') => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/find-renters/listings/`, {
            params: q ? { q } : {},
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching find-renters listings:', error);
        throw error;
    }
};

/**
 * Headline renter-match counts for a lease listing (per-property badge).
 * @param {number|string} propertyId
 * @returns {Promise<{total, strong, within_30_days, strong_threshold}>}
 */
export const fetchRenterMatchCount = async (propertyId) => {
    const response = await api.get(`${ANALYTICS_BASE_URL}/find-renters/match-count/`, {
        params: { property_id: propertyId },
    });
    return response.data;
};

/**
 * Fetch the suburb tick-box list for renters — same shape as
 * fetchFindBuyersSuburbs, fed from RentingPreference instead.
 * @param {{ suburb?, state?, lat?, lng?, radiusKm? }} opts
 * @returns {Promise<{radius_km, origin_resolved, target, suburbs: Array}>}
 */
export const fetchFindRentersSuburbs = async ({ suburb, state, lat, lng, radiusKm = 5 } = {}) => {
    try {
        const params = { radius_km: radiusKm };
        if (suburb) params.suburb = suburb;
        if (state) params.state = state;
        if (lat != null) params.lat = lat;
        if (lng != null) params.lng = lng;
        const response = await api.get(`${ANALYTICS_BASE_URL}/find-renters/suburbs/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching find-renters suburbs:', error);
        throw error;
    }
};

/**
 * Rank the renters whose saved search matches the chosen property + ticked
 * suburbs.
 * @param {Object} payload - { property_id?, suburb, state, lat, lng, radius_km,
 *   rent_min, rent_max, beds_min, beds_max, baths_min, baths_max,
 *   property_types: [..], portal_category, suburbs: [..] }
 * @returns {Promise<{target, selected_suburbs, count, results: Array}>}
 */
export const findRentersForProperty = async (payload) => {
    try {
        const response = await api.post(`${ANALYTICS_BASE_URL}/find-renters/`, payload);
        return response.data;
    } catch (error) {
        console.error('Error finding renters for property:', error);
        throw error;
    }
};

/**
 * Edit a matched renter's saved preferences from the results list. Always
 * writes to RentingPreference (the one renter source).
 * @param {Object} payload - { ref_id, suburbs:[], rent_min, rent_max,
 *   beds_min, beds_max, baths, property_types:[] }
 * @returns {Promise}
 */
export const editRenterPreferences = async (payload) => {
    try {
        const response = await api.patch(`${ANALYTICS_BASE_URL}/find-renters/preferences/`, payload);
        return response.data;
    } catch (error) {
        console.error('Error editing renter preferences:', error);
        throw error;
    }
};

/**
 * Email or text all the matched renters (or the shortlist) in one send. Same
 * pipeline/consent/kill-switch as messageBuyerMatches.
 * @param {Object} payload - { channel: 'email'|'sms', subject, body,
 *   recipients: [{ ref_id, contact_id, email, phone, name }] }
 * @returns {Promise<{total, reachable, queued, sent, failed, skipped, simulated}>}
 */
export const messageRenterMatches = async (payload) => {
    try {
        const response = await api.post(`${ANALYTICS_BASE_URL}/find-renters/message/`, payload);
        return response.data;
    } catch (error) {
        console.error('Error messaging renter matches:', error);
        throw error;
    }
};

/**
 * Fetch the list of accounts excluded from ALL analytics.
 * @returns {Promise<Array>}
 */
export const fetchAnalyticsExclusions = async () => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/exclusions/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching analytics exclusions:', error);
        throw error;
    }
};

/**
 * Search accounts (ALL roles — buyers, agents, admins) to add to the
 * exclusion list. The buyers-only endpoint could not surface staff accounts.
 * @param {string} term - Name, email or phone search term
 * @returns {Promise<Array>}
 */
export const searchExclusionCandidates = async (term) => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/exclusions/`, {
            params: { search: term },
        });
        return response.data;
    } catch (error) {
        console.error('Error searching exclusion candidates:', error);
        throw error;
    }
};

/**
 * Exclude an account from analytics.
 * @param {number} buyerId - CustomUser id to exclude
 * @param {string} [reason] - Optional note
 * @param {Object} [scope] - { excludeFromPortal, excludeFromCrm }, both default true
 * @returns {Promise}
 */
export const addAnalyticsExclusion = async (buyerId, reason = '', scope = {}) => {
    try {
        // Only send a flag when the caller states one. Left out, the server
        // picks the safe default: portal always, CRM only for accounts that
        // aren't a live agent/admin/PA.
        const payload = { buyer_id: buyerId, reason };
        if (scope.excludeFromPortal !== undefined) {
            payload.exclude_from_portal = scope.excludeFromPortal;
        }
        if (scope.excludeFromCrm !== undefined) {
            payload.exclude_from_crm = scope.excludeFromCrm;
        }
        const response = await api.post(`${ANALYTICS_BASE_URL}/exclusions/`, payload);
        return response.data;
    } catch (error) {
        console.error('Error adding analytics exclusion:', error);
        throw error;
    }
};

/**
 * Change which analytics environments an exclusion applies to.
 * @param {number} exclusionId - Exclusion row id
 * @param {Object} scope - { exclude_from_portal?, exclude_from_crm? }
 * @returns {Promise}
 */
export const updateAnalyticsExclusionScope = async (exclusionId, scope) => {
    try {
        const response = await api.patch(
            `${ANALYTICS_BASE_URL}/exclusions/${exclusionId}/`,
            scope,
        );
        return response.data;
    } catch (error) {
        console.error('Error updating analytics exclusion:', error);
        throw error;
    }
};

/**
 * Remove an analytics exclusion (re-include the account).
 * @param {number} exclusionId - Exclusion row id
 * @returns {Promise}
 */
export const removeAnalyticsExclusion = async (exclusionId) => {
    try {
        const response = await api.delete(`${ANALYTICS_BASE_URL}/exclusions/${exclusionId}/`);
        return response.data;
    } catch (error) {
        console.error('Error removing analytics exclusion:', error);
        throw error;
    }
};

/**
 * Fetch portal sign-ups attributed to geo SEO pages (by suburb/page).
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @returns {Promise}
 */
export const fetchGeoSeoSignups = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/geo-seo-signups/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching geo SEO sign-ups:', error);
        throw error;
    }
};

/**
 * Fetch traditional (Google organic) SEO channel analytics.
 */
export const fetchTraditionalSeoSignups = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/traditional-seo-signups/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching traditional SEO analytics:', error);
        throw error;
    }
};

/**
 * Fetch per-channel drill-down analytics for ANY Traffic Sources row
 * (Instagram, Facebook, Apple Ads, etc.), scoped to a utm_source (+ medium).
 * Same payload shape as the SEO channel endpoints.
 * @param {string} source - utm_source value (required)
 * @param {string} [medium] - utm_medium value
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @param {string} [label] - display label for the channel
 */
export const fetchChannelAnalytics = async (source, medium, startDate, endDate, label) => {
    try {
        const params = { source };
        if (medium) params.medium = medium;
        if (label) params.label = label;
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/channel/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching channel analytics:', error);
        throw error;
    }
};

// ── Engagement tab ────────────────────────────────────────────────────────
// Retention, user-journey funnel, lead-source attribution and GeeVee question
// categories. All accept the same ISO start/end range as the rest of the tab.

const _rangeParams = (startDate, endDate) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return params;
};

/**
 * Fetch retention metrics: new vs returning users, sign-in distribution,
 * days-between-visits buckets.
 */
export const fetchRetention = async (startDate, endDate) => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/engagement/retention/`,
            { params: _rangeParams(startDate, endDate) });
        return response.data;
    } catch (error) {
        console.error('Error fetching retention metrics:', error);
        throw error;
    }
};

/**
 * Fetch the 6-stage user-journey funnel
 * (Signup → Viewed → Saved → Calculator → GeeVee → Enquiry).
 */
export const fetchUserJourney = async (startDate, endDate) => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/engagement/journey/`,
            { params: _rangeParams(startDate, endDate) });
        return response.data;
    } catch (error) {
        console.error('Error fetching user journey:', error);
        throw error;
    }
};

/**
 * Fetch lead-source attribution (which channel each enquiry came from).
 */
export const fetchLeadSources = async (startDate, endDate) => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/engagement/lead-sources/`,
            { params: _rangeParams(startDate, endDate) });
        return response.data;
    } catch (error) {
        console.error('Error fetching lead sources:', error);
        throw error;
    }
};

/**
 * Signups attributed to open-home QR or invite email, grouped by agent.
 */
export const fetchOpenHomeSignups = async (startDate, endDate) => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/open-home-signups/`,
            { params: _rangeParams(startDate, endDate) });
        return response.data;
    } catch (error) {
        console.error('Error fetching open home signups:', error);
        throw error;
    }
};

/**
 * Signups attributed to individual agents' personal portal-signup QR codes
 * (utm_source=real_estate_agent), grouped by agent. Backs the "Real Estate
 * Agents" Traffic Sources drill-down.
 */
export const fetchAgentSignups = async (startDate, endDate) => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/agent-signups/`,
            { params: _rangeParams(startDate, endDate) });
        return response.data;
    } catch (error) {
        console.error('Error fetching agent signups:', error);
        throw error;
    }
};

/**
 * Fetch GeeVee question categories (what buyers ask the AI about).
 */
export const fetchGeeVeeCategories = async (startDate, endDate) => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/engagement/geevee-categories/`,
            { params: _rangeParams(startDate, endDate) });
        return response.data;
    } catch (error) {
        console.error('Error fetching GeeVee categories:', error);
        throw error;
    }
};

/**
 * Fetch the "Who to call" smart call list for the dashboard.
 * Agents get their own referred buyers; admins get the unassigned pool,
 * so the two never overlap.
 * @param {number} limit - max buyers (default 15)
 * @param {number} days - activity look-back window (default 30)
 * @param {string} [perspective] - 'mine' | 'unassigned' to override role default
 * @returns {Promise<{perspective, generated_at, window_days, count, buyers}>}
 */
export const fetchSmartCallList = async (limit = 15, days = 30, perspective) => {
    try {
        const params = { limit, days };
        if (perspective) params.perspective = perspective;
        const response = await api.get(`${ANALYTICS_BASE_URL}/call-list/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching smart call list:', error);
        throw error;
    }
};

/**
 * Mark a Who-to-Call card as contacted so it drops off the current agent's call
 * list, AND log it as a proper call (the same rich call log as the property tabs'
 * Called button: a note, a last-contacted stamp, a typed call on the timeline for
 * the KPIs, and an optional call-back reminder). Pass buyerId for an
 * activity-driven card, reminderId for a reminder-driven card (completes the
 * reminder), attendeeId for an open-home card, leadId for a Meta lead, or any
 * combination the card carries. contactId (resolved on the FE) lets the backend
 * log the call for buyer/activity cards; outcome is the note text the agent
 * picked and remindInDays optionally schedules the call back.
 * @param {{ buyerId?: number, reminderId?: number, attendeeId?: number,
 *   leadId?: number, contactId?: number, outcome?: string, remindInDays?: number }} opts
 */
export const markBuyerContacted = async ({
    buyerId, reminderId, attendeeId, leadId, contactId, outcome, remindInDays,
} = {}) => {
    try {
        const response = await api.post(`${ANALYTICS_BASE_URL}/call-list/contacted/`, {
            buyer_id: buyerId,
            reminder_id: reminderId,
            // Open-home cards are keyed to an inspection attendee — those buyers
            // often have no portal account, so there's no buyer_id to dismiss.
            attendee_id: attendeeId,
            // A pinned Meta (Facebook) lead card: stamps the lead as contacted.
            lead_id: leadId,
            // Lets the backend log the rich call for buyer/activity cards.
            contact_id: contactId,
            outcome,
            remind_in_days: remindInDays,
        });
        return response.data;
    } catch (error) {
        console.error('Error marking buyer contacted:', error);
        throw error;
    }
};

/**
 * "Remove from list" on a Who-to-Call card: hides it from the agent's own list
 * WITHOUT logging a call or setting a call-back (CRM-2026-00122). Due call-backs
 * are marked done; portal buyers and open-home attendees come back only if they
 * do something new.
 */
export const removeFromCallList = async ({ buyerId, reminderId, attendeeId } = {}) => {
    try {
        const response = await api.post(`${ANALYTICS_BASE_URL}/call-list/remove/`, {
            buyer_id: buyerId,
            reminder_id: reminderId,
            attendee_id: attendeeId,
        });
        return response.data;
    } catch (error) {
        console.error('Error removing from call list:', error);
        throw error;
    }
};

/**
 * The signed-in agent's own "Who to Call" settings: which of their listings put
 * buyers on their list. Teams are set up differently: on some the lead (first)
 * agent works every buyer call, on others the second agent does.
 * @returns {Promise<{listing_scope, is_default, lead_listings, second_listings}>}
 */
export const fetchCallListSettings = async () => {
    try {
        const response = await api.get(`${ANALYTICS_BASE_URL}/call-list/settings/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching call list settings:', error);
        throw error;
    }
};

/**
 * Save the agent's Who-to-Call settings. Send either toggle on its own, and the
 * backend keeps whatever the other one currently is.
 * @param {{ leadListings?: boolean, secondListings?: boolean, listingScope?: string }} opts
 */
export const updateCallListSettings = async ({ leadListings, secondListings, listingScope } = {}) => {
    try {
        const body = {};
        if (leadListings !== undefined) body.lead_listings = leadListings;
        if (secondListings !== undefined) body.second_listings = secondListings;
        if (listingScope !== undefined) body.listing_scope = listingScope;
        const response = await api.patch(`${ANALYTICS_BASE_URL}/call-list/settings/`, body);
        return response.data;
    } catch (error) {
        console.error('Error saving call list settings:', error);
        throw error;
    }
};

/**
 * Drill-down list for the retention charts.
 * @param {string} detail - 'users' | 'signins' | 'days_between'
 * @param {object} opts - { segment } for users, { bucket } for signins/days_between
 * @returns {Promise<{detail, rows}>}
 */
export const fetchRetentionDetail = async (startDate, endDate, detail, opts = {}) => {
    try {
        const params = { ..._rangeParams(startDate, endDate), detail, ...opts };
        const response = await api.get(`${ANALYTICS_BASE_URL}/engagement/retention/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching retention detail:', error);
        throw error;
    }
};

/**
 * Drill-down list of the actual GeeVee questions in one category.
 * @param {string} category - category slug, or '__uncategorized__'
 * @returns {Promise<{detail, rows}>}
 */
export const fetchGeeVeeCategoryMessages = async (startDate, endDate, category) => {
    try {
        const params = { ..._rangeParams(startDate, endDate), detail: 'messages', category };
        const response = await api.get(`${ANALYTICS_BASE_URL}/engagement/geevee-categories/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching GeeVee category messages:', error);
        throw error;
    }
};

/**
 * Fetch churn feedback — why buyers leave (deactivate / unsubscribe reasons).
 * @param {string} [flow] - 'deactivate' | 'unsubscribe' to narrow to one flow
 * @returns {Promise<{range, total_submissions, by_flow, by_reason}>}
 */
export const fetchChurnFeedback = async (startDate, endDate, flow) => {
    try {
        const params = { ..._rangeParams(startDate, endDate) };
        if (flow) params.flow = flow;
        const response = await api.get(`${ANALYTICS_BASE_URL}/engagement/churn-feedback/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching churn feedback:', error);
        throw error;
    }
};

/**
 * Drill-down list of the individual submissions behind one churn reason.
 * @param {string} reason - reason key
 * @param {string} [flow] - 'deactivate' | 'unsubscribe'
 * @returns {Promise<{detail, rows}>}
 */
export const fetchChurnFeedbackDetail = async (startDate, endDate, reason, flow) => {
    try {
        const params = { ..._rangeParams(startDate, endDate), detail: 'submissions', reason };
        if (flow) params.flow = flow;
        const response = await api.get(`${ANALYTICS_BASE_URL}/engagement/churn-feedback/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching churn feedback detail:', error);
        throw error;
    }
};

/**
 * User Profiles tab — aggregate unified-profile segment counts (status +
 * activity + cross-tabs) plus top suburbs across all portal buyers.
 * @returns {Promise<{range, total_buyers, groups, top_suburbs}>}
 */
export const fetchUserProfileSegments = async (startDate, endDate) => {
    try {
        const params = _rangeParams(startDate, endDate);
        const response = await api.get(`${ANALYTICS_BASE_URL}/user-profiles/segments/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching user profile segments:', error);
        throw error;
    }
};

/**
 * Drill-down: the buyers inside one unified-profile segment.
 * @param {string} segment - segment key (e.g. 'landlord', 'finance_ready')
 * @returns {Promise<{segment, count, rows}>}
 */
export const fetchUserProfileSegmentList = async (startDate, endDate, segment) => {
    try {
        const params = { ..._rangeParams(startDate, endDate), segment };
        const response = await api.get(`${ANALYTICS_BASE_URL}/user-profiles/list/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching user profile segment list:', error);
        throw error;
    }
};

/**
 * Buyers Advocate Metrics — headline numbers over contacts flagged as buyer
 * advocates. Three metrics are live; three are placeholders (available=false)
 * until the advocate program is built.
 * @returns {Promise<{range, total_signed_up, matched_accounts, metrics}>}
 */
export const fetchBuyerAdvocateMetrics = async (startDate, endDate) => {
    try {
        const params = _rangeParams(startDate, endDate);
        const response = await api.get(`${ANALYTICS_BASE_URL}/buyer-advocates/metrics/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching buyer advocate metrics:', error);
        throw error;
    }
};

/**
 * Targeting Brief — the agent sourcing checklist: priority suburbs by buyer
 * demand, finance readiness, buyer types, bedroom/property-type demand, the
 * portal investment benchmark, plus nightly LLM advice. (admin + agent)
 * @returns {Promise}
 */
export const fetchTargetingBrief = async (startDate, endDate) => {
    try {
        const params = _rangeParams(startDate, endDate);
        const response = await api.get(`${ANALYTICS_BASE_URL}/targeting-brief/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching targeting brief:', error);
        throw error;
    }
};

/**
 * Investor Demand Index — "What Investors Are Actually Looking For": avg target
 * yield & budget, most-searched suburbs / asset types / strategies across the
 * investor cohort, plus their calculator benchmark. (admin + agent)
 * @returns {Promise}
 */
export const fetchInvestorDemandIndex = async (startDate, endDate) => {
    try {
        const params = _rangeParams(startDate, endDate);
        const response = await api.get(`${ANALYTICS_BASE_URL}/investor-demand-index/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching investor demand index:', error);
        throw error;
    }
};

/**
 * Drill-down: the investors behind one Investor Demand Index ranking bar.
 * @param {string} segment - the item's `segment_key`, e.g. 'inv_suburb:Melbourne',
 *                           'inv_asset:House', 'inv_strategy:high_yield'
 * @returns {Promise<{segment, count, rows}>}
 */
export const fetchInvestorDemandIndexList = async (startDate, endDate, segment) => {
    try {
        const params = { ..._rangeParams(startDate, endDate), segment };
        const response = await api.get(`${ANALYTICS_BASE_URL}/investor-demand-index/list/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching investor demand index list:', error);
        throw error;
    }
};

/**
 * Drill-down: the buyers behind one targeting-brief number.
 * @param {string} segment - e.g. 'investors', 'finance:cash', 'suburb:Northcote',
 *                           'property_type:House', 'bedroom:3'
 * @returns {Promise<{segment, count, rows}>}
 */
export const fetchTargetingBriefList = async (startDate, endDate, segment) => {
    try {
        const params = { ..._rangeParams(startDate, endDate), segment };
        const response = await api.get(`${ANALYTICS_BASE_URL}/targeting-brief/list/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching targeting brief list:', error);
        throw error;
    }
};

/**
 * Portfolio tab — aggregate Portfolio Tracker analytics across all investors:
 * headline value/equity/yield/cashflow KPIs, loan profile, type/state/ownership
 * breakdowns, and investor goal/horizon/risk distributions.
 * @returns {Promise<{range, totals, by_property_type, by_state, by_ownership_entity, by_status, goals, horizons, risks, top_suburbs}>}
 */
export const fetchPortfolioAnalytics = async (startDate, endDate) => {
    try {
        const params = _rangeParams(startDate, endDate);
        const response = await api.get(`${ANALYTICS_BASE_URL}/portfolio/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching portfolio analytics:', error);
        throw error;
    }
};

// ── Heatmap tab ───────────────────────────────────────────────────────────
// Screen tracking: most-visited portal screens + per-screen click heatmap.

/**
 * Fetch the "most visited screens" ranking (views, visitors, avg time on page,
 * avg scroll depth, click volume).
 * @param {string} startDate - ISO datetime
 * @param {string} endDate - ISO datetime
 * @param {('all'|'desktop'|'mobile')} [device]
 * @returns {Promise<{total_views, total_visitors, pages: [], capped}>}
 */
export const fetchHeatmapPages = async (startDate, endDate, device = 'all') => {
    try {
        const params = { device };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/heatmap/pages/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching heatmap pages:', error);
        throw error;
    }
};

/**
 * Fetch the click heatmap for one screen: density grid points, top clicked
 * elements, rage/dead-click counts and scroll depth.
 * @param {string} path - normalized screen path (e.g. '/home', '/property/:id')
 * @param {string} startDate - ISO datetime
 * @param {string} endDate - ISO datetime
 * @param {('all'|'desktop'|'mobile')} [device]
 * @returns {Promise<{points, elements, rage_count, dead_count, avg_scroll_pct, max_weight}>}
 */
export const fetchHeatmapClicks = async (path, startDate, endDate, device = 'all') => {
    try {
        const params = { path, device };
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${ANALYTICS_BASE_URL}/heatmap/clicks/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching heatmap clicks:', error);
        throw error;
    }
};

/**
 * Fetch the cached full-page screenshot used as the backdrop behind the heatmap
 * for one screen. Captured server-side (logged-in headless browser). On a cache
 * miss the backend kicks off a capture and returns status 'pending' — poll until
 * 'ready'. Returns 'disabled' if the screenshot service isn't configured.
 * @param {string} path - normalized screen path (e.g. '/property/:id')
 * @param {boolean} [refresh] - force a re-capture even if a fresh one is cached
 * @returns {Promise<{status:'ready'|'pending'|'failed'|'disabled', url?, aspect?, concrete_url?, captured_at?, error?}>}
 */
export const fetchHeatmapScreenshot = async (path, refresh = false) => {
    try {
        const params = { path };
        if (refresh) params.refresh = 1;
        const response = await api.get(`${ANALYTICS_BASE_URL}/heatmap/screenshot/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching heatmap screenshot:', error);
        throw error;
    }
};

// ── Cost Per Lead tab ─────────────────────────────────────────────────────
// Ad-spend acquisition analytics: CAC/ROAS cards, the user-journey funnel and
// the campaign performance table. Served by the ad_spend app at /api/ad-spend/.

const COST_BASE_URL = '/api/ad-spend';

/**
 * Fetch the cost-per-lead dashboard: cards, funnel and campaign table in one call.
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @param {string} [platform] - meta|google|tiktok|manual
 * @returns {Promise<{range, cards, funnel, campaigns}>}
 */
export const fetchCostDashboard = async (startDate, endDate, platform) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        if (platform) params.platform = platform;
        const response = await api.get(`${COST_BASE_URL}/dashboard/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching cost-per-lead dashboard:', error);
        throw error;
    }
};

/**
 * Fetch the campaigns the system has already seen (from tracked events + existing
 * ad campaigns) to populate the spend-entry dropdown — no UTM knowledge needed.
 * @returns {Promise<{campaigns: Array<{utm_campaign, label, events, source, platform, name}>}>}
 */
export const fetchKnownCampaigns = async () => {
    try {
        const response = await api.get(`${COST_BASE_URL}/known-campaigns/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching known campaigns:', error);
        throw error;
    }
};

/**
 * Record one campaign-day of ad spend by hand (idempotent per platform+campaign+date).
 * Lets the dashboard light up before the Meta/Google APIs are connected.
 * @param {{platform, utm_campaign, campaign_name?, date, spend, clicks?, impressions?}} payload
 * @returns {Promise}
 */
export const addManualSpend = async (payload) => {
    try {
        const response = await api.post(`${COST_BASE_URL}/spend/manual/`, payload);
        return response.data;
    } catch (error) {
        console.error('Error adding manual spend:', error);
        throw error;
    }
};

/**
 * List the recorded ad-spend entries (most recent first) so they can be reviewed
 * or removed — e.g. deleting a test campaign keyed in with fake numbers.
 * @returns {Promise<Array<{id, campaign, campaign_name, platform, date, spend, clicks, impressions, source}>>}
 */
export const fetchSpendEntries = async () => {
    try {
        const response = await api.get(`${COST_BASE_URL}/spend/manual/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching spend entries:', error);
        throw error;
    }
};

/**
 * Delete one recorded spend entry by id. If it was the last spend behind a
 * hand-created campaign, the backend removes the empty campaign too.
 * @param {number} id - AdSpendDaily row id
 * @returns {Promise}
 */
export const deleteSpendEntry = async (id) => {
    try {
        const response = await api.delete(`${COST_BASE_URL}/spend/manual/${id}/`);
        return response.data;
    } catch (error) {
        console.error('Error deleting spend entry:', error);
        throw error;
    }
};

// ── Suburb Research tab ────────────────────────────────────────────────────
// What suburbs buyers research on the portal's CoinMarketCap-style Research
// page: most-opened suburbs, raw search queries, and daily activity. Served by
// the suburb_intelligence app at /api/suburb-intelligence/analytics/.

/**
 * Fetch suburb-research search intelligence.
 * @param {string} startDate - ISO datetime string
 * @param {string} endDate - ISO datetime string
 * @returns {Promise<{totals, top_viewed, top_searched, daily}>}
 */
export const fetchSuburbResearchAnalytics = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get('/api/suburb-intelligence/analytics/', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching suburb research analytics:', error);
        throw error;
    }
};

/**
 * Off-Market Demand Index — "Most Wanted Off-Market Areas": suburbs ranked by
 * real off-market buyer demand (views / saves / enquiries / offers on
 * off-market stock). Served by the suburb_intelligence app. (admin + agent)
 * @returns {Promise<{count, totals, results}>}
 */
export const fetchOffMarketDemand = async (limit = 20) => {
    try {
        const response = await api.get('/api/suburb-intelligence/off-market-demand/', {
            params: { limit },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching off-market demand index:', error);
        throw error;
    }
};

/**
 * Fetch chatbot analytics (GeeVee, website chatbot, messenger)
 * @param {number} days - Lookback window in days
 * @returns {Promise}
 */
export const fetchChatbotAnalytics = async (days = 30) => {
    try {
        const response = await api.get('/api/chatbot/analytics/', { params: { days } });
        return response.data;
    } catch (error) {
        console.error('Error fetching chatbot analytics:', error);
        throw error;
    }
};

/**
 * Fetch chatbot sessions for analytics drill-down
 * @param {number} days - Lookback window
 * @param {string} filter - all|completed|buyer_portal|messenger|with_leads|unique_users|with_transcript
 * @param {boolean} includeMessages - Include full message thread per session
 * @param {number} limit - Max sessions to return
 */
export const fetchChatbotAnalyticsSessions = async (days = 30, filter = 'all', includeMessages = true, limit = 200) => {
    try {
        const response = await api.get('/api/chatbot/analytics/sessions/', {
            params: {
                days,
                filter,
                include_messages: includeMessages ? 1 : 0,
                limit,
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching chatbot analytics sessions:', error);
        throw error;
    }
};

/**
 * Fetch the full campaign analytics payload for ONE property.
 * Powers the property Analytics tab: summary metrics, funnel, buyer intent
 * scores, engagement over time, campaign health, repeat behaviour, source
 * attribution, open homes, benchmark and agent alerts.
 *
 * @param {number|string} propertyId
 * @param {string} period  '7' | '30' | 'all'
 * @param {boolean} refresh  skip the server-side cache
 */
export const fetchPropertyCampaignAnalytics = async (propertyId, period = '30', refresh = false) => {
    try {
        const response = await api.get(
            `${ANALYTICS_BASE_URL}/property/${propertyId}/campaign/`,
            { params: { period, ...(refresh ? { refresh: 1 } : {}) } },
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching property campaign analytics:', error);
        throw error;
    }
};

/**
 * Generate the vendor-facing campaign report PDF for a property.
 * Returns { filename, pdf_base64 }, or { status: 'sent' } when emailTo is set.
 *
 * @param {number|string} propertyId
 * @param {Object} options { period, commentary, narrative, preparedFor, emailTo,
 *   sections, hidden, priceChart, showBuyerNames } where sections is the array of
 *   section keys to include, hidden is { tiles, movement, funnel, top_activity,
 *   open_homes, benchmark, price_feedback, buyer_comments } arrays of individual
 *   rows to leave out, priceChart is the pricing feedback graph the agent redrew
 *   ([{ label, count }], null = use the one read from the open home notes) and
 *   showBuyerNames prints buyers' first names against their comments.
 */
export const generatePropertyVendorReport = async (propertyId, {
    period = '30', commentary = '', narrative = '', preparedFor = '', emailTo = '',
    sections = null, hidden = {}, priceChart = null, showBuyerNames = false,
} = {}) => {
    try {
        const response = await api.post(
            `${ANALYTICS_BASE_URL}/property/${propertyId}/vendor-report/`,
            {
                period,
                commentary,
                ...(narrative ? { narrative } : {}),
                ...(preparedFor ? { prepared_for: preparedFor } : {}),
                ...(sections ? { sections } : {}),
                ...Object.fromEntries(
                    Object.entries(hidden || {})
                        .filter(([, keys]) => keys && keys.length)
                        .map(([bucket, keys]) => [`hidden_${bucket}`, keys]),
                ),
                ...(priceChart && priceChart.length ? { price_chart: priceChart } : {}),
                ...(showBuyerNames ? { show_buyer_names: true } : {}),
                ...(emailTo ? { email_to: emailTo } : {}),
            },
        );
        return response.data;
    } catch (error) {
        console.error('Error generating vendor report:', error);
        throw error;
    }
};

/**
 * What the vendor campaign report WOULD contain for this property and period:
 * the sections, the individual rows inside them, and the campaign summary
 * paragraph. Feeds the tick boxes in the vendor report modal so the agent edits
 * the document before the PDF is made. Aggregate counts only, no buyers.
 *
 * @param {number|string} propertyId
 * @param {string} period '7' | '30' | 'all'
 * @returns {Promise<{sections, default_sections, narrative, health, rows,
 *   price_chart, price_feedback, cover}>}
 */
export const previewPropertyVendorReport = async (propertyId, period = '30') => {
    try {
        const response = await api.post(
            `${ANALYTICS_BASE_URL}/property/${propertyId}/vendor-report/`,
            { period, preview: true },
        );
        return response.data;
    } catch (error) {
        console.error('Error loading vendor report options:', error);
        throw error;
    }
};

// ── Facebook / Meta lead ads ─────────────────────────────────────────────────
const LEAD_INGEST_BASE_URL = '/api/lead-ingest';

/**
 * Everything the Facebook Leads tab needs, in one call: the tiles, the daily
 * trend, the campaign / form / agent breakdowns and the most recent leads.
 *
 * Test-mode payloads are excluded by the backend, so what you see here is only
 * real enquiries.
 *
 * @param {string} startDate ISO datetime
 * @param {string} endDate   ISO datetime
 * @returns {Promise<Object>}
 */
export const fetchInboundLeadStats = async (startDate, endDate) => {
    try {
        const params = {};
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        const response = await api.get(`${LEAD_INGEST_BASE_URL}/inbound/stats/`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching inbound lead stats:', error);
        throw error;
    }
};

/**
 * The raw payload behind one inbound lead, for when a lead looks wrong and
 * somebody needs to see exactly what Facebook sent.
 *
 * @param {number} id InboundLead id
 * @returns {Promise<Object>}
 */
export const fetchInboundLeadDetail = async (id) => {
    try {
        const response = await api.get(`${LEAD_INGEST_BASE_URL}/inbound/${id}/`);
        return response.data;
    } catch (error) {
        console.error('Error fetching inbound lead detail:', error);
        throw error;
    }
};

/**
 * Re-run a lead the CRM failed to process, after the cause has been fixed.
 * Admin only, and safe to repeat: an already-processed lead is left alone.
 *
 * @param {number} id InboundLead id
 * @returns {Promise<Object>}
 */
export const replayInboundLead = async (id) => {
    try {
        const response = await api.post(`${LEAD_INGEST_BASE_URL}/inbound/${id}/replay/`);
        return response.data;
    } catch (error) {
        console.error('Error replaying inbound lead:', error);
        throw error;
    }
};

/**
 * What this backend can export, and what the caller is allowed to export.
 *
 * The dialog asks rather than hard-coding a list, so a tab added (or a scope
 * taken away) on the backend cannot leave the export offering something that
 * is not there.
 *
 * @returns {Promise<{scopes: Array<{id, label, datasets: Array<{key, label}>}>}>}
 */
export const fetchAnalyticsExportOptions = async () => {
    const response = await api.get(`${ANALYTICS_BASE_URL}/export/options/`);
    return response.data;
};

/**
 * Download one analytics environment for a date range.
 *
 * Returns the raw file, so the caller is responsible for turning the blob into
 * a download. `file_format` is deliberately NOT called `format`: DRF reserves
 * that query param for content negotiation and answers 404 to a value it has
 * no renderer for.
 *
 * @param {Object} opts
 * @param {'portal'|'crm'|'executive'} opts.scope
 * @param {string} opts.startDate YYYY-MM-DD
 * @param {string} opts.endDate   YYYY-MM-DD
 * @param {string[]} [opts.datasets] dataset keys; omit for everything
 * @param {'xlsx'|'csv'|'html'} [opts.fileFormat] html is a standalone report page
 * @returns {Promise<import('axios').AxiosResponse<Blob>>}
 */
export const downloadAnalyticsExport = ({
    scope, startDate, endDate, datasets, fileFormat = 'xlsx',
}) => {
    const params = {
        scope,
        start_date: startDate,
        end_date: endDate,
        file_format: fileFormat,
    };
    if (datasets && datasets.length) params.datasets = datasets.join(',');
    return api.get(`${ANALYTICS_BASE_URL}/export/`, { params, responseType: 'blob' });
};
